import { useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import styled from "styled-components";
import theme from "../styles/theme";
import Button from "./Button";
import FlexBox, { FlexGrow } from "./FlexBox";
import Input, { InputTextArea } from "./Input";
import { StyledText } from "./Text";
import { ClickableIcon } from "./Icon";
import { useScreenClass } from "react-grid-system";
import Modal from "./Modal";
type VersionEditType = {
  isOpen?: boolean;
  header: string;
  mode: "modify" | "create";
  setOpen: (x: boolean) => void;
  versionData?: any;
  onSaveComplete: (data: any) => void;
  isDisableButton?: boolean;
};

const Header = styled.h4`
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: left;
`;
const CustomFlex = styled(FlexBox)`
  padding: 20px 0;
  align-items: center;
  border-bottom: 1px solid #3c406c;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 10px 0;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 350px;
  flex-grow: 0;
`;
const Label = styled.h5`
  font-size: 14px;
  font-style: normal;
  font-weight: bold;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
interface SkillwInputs {
  core_credits: number;
  hss_credits: number;
  math_credits: number;
  max_credits: number;
  min_credits: number;
  rnd_credits: number;
  xp_credits: number;
  title: string;
}

const VersionEdit = ({
  isOpen,
  setOpen,
  header,
  mode,
  versionData,
  onSaveComplete,
  isDisableButton,
}: VersionEditType): JSX.Element => {
  const screenClass = useScreenClass();
  const [openTopic, setOpenTopic] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SkillwInputs>({
    mode: "onBlur",
    defaultValues: {
      core_credits: versionData ? versionData.core_credits : 0,
      hss_credits: versionData ? versionData.hss_credits : 0,
      math_credits: versionData ? versionData.math_credits : 0,
      max_credits: versionData ? versionData.max_credits : 0,
      min_credits: versionData ? versionData.min_credits : 0,
      rnd_credits: versionData ? versionData.rnd_credits : 0,
      xp_credits: versionData ? versionData.xp_credits : 0,
      title: versionData ? versionData.title : "",
    },
  });
  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const onSubmitOverview: SubmitHandler<SkillwInputs> = (fdata: any) => {
    onSaveComplete(fdata);
  };
  return (
    <>
      <Modal
        isOpen={isOpen || false}
        setOpen={setOpen}
        customStyle={{
          overlay: {
            zIndex: "999958",
          },
          content: {
            marginTop: 10,
            maxWidth: "700px",
            padding: "0 24px",
            color: "black",
            background: "#FFFFFF",
            border: "1px solid #E7EBEE",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.07)",
            borderRadius: "8px",
          },
        }}
      >
        <CustomFlex>
          <FlexGrow>
            <Header>{header}</Header>
          </FlexGrow>
          <ClickableIcon
            size={3.125}
            fixedWidth={false}
            color="black"
            name="close"
            onClick={() => setOpen(false)}
          />
        </CustomFlex>
        <CustomFlex
          style={{
            borderBottom: "none",
            alignItems: "flex-start",
            overflowY: "scroll",
          }}
        >
          <form
            style={{ width: "100%" }}
            onSubmit={handleSubmit(onSubmitOverview)}
          >
            <FlexGrow>
              <div
                style={{
                  display: "flex",
                  gap: 20,
                  alignItems: "center",
                  color: theme.color.lightBlueGray800,
                  fontWeight: 500,
                  marginBottom: 20,
                }}
              >
                <div><span style={{fontWeight:'bold'}}>University :</span> {versionData.university}</div>
                <div><span style={{fontWeight:'bold'}}>Program :</span> {versionData.program_name}</div>
                <div><span style={{fontWeight:'bold'}}>Version :</span> {versionData.display_version}</div>
              </div>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Title</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <Input
                    required
                    style={{ width: "100%" }}
                    isFullWidth
                    {...register("title", { required: true })}
                  />
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Minimum credits required</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <FlexBox
                    space={0}
                    alignItems="end"
                    style={{ width: "100%" }}
                    direction="column"
                  >
                    <Input
                      min={0}
                      type="number"
                      required
                      style={{ width: "100%" }}
                      isFullWidth
                      {...register("min_credits", { required: true })}
                    />
                  </FlexBox>
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Maximum credits allowed</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <FlexBox
                    space={0}
                    alignItems="end"
                    style={{ width: "100%" }}
                    direction="column"
                  >
                    <Input
                      min={0}
                      type="number"
                      required
                      style={{ width: "100%" }}
                      isFullWidth
                      {...register("max_credits", { required: true })}
                    />
                  </FlexBox>
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Core credits required</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <FlexBox
                    space={0}
                    alignItems="end"
                    style={{ width: "100%" }}
                    direction="column"
                  >
                    <Input
                      min={0}
                      type="number"
                      required
                      style={{ width: "100%" }}
                      isFullWidth
                      {...register("core_credits", { required: true })}
                    />
                  </FlexBox>
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Math and science credits required</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <FlexBox
                    space={0}
                    alignItems="end"
                    style={{ width: "100%" }}
                    direction="column"
                  >
                    <Input
                      min={0}
                      type="number"
                      required
                      style={{ width: "100%" }}
                      isFullWidth
                      {...register("math_credits", { required: true })}
                    />
                  </FlexBox>
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Humanities credits required</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <FlexBox
                    space={0}
                    alignItems="end"
                    style={{ width: "100%" }}
                    direction="column"
                  >
                    <Input
                      min={0}
                      type="number"
                      required
                      style={{ width: "100%" }}
                      isFullWidth
                      {...register("hss_credits", { required: true })}
                    />
                  </FlexBox>
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Research and development credits required</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <FlexBox
                    space={0}
                    alignItems="end"
                    style={{ width: "100%" }}
                    direction="column"
                  >
                    <Input
                      min={0}
                      type="number"
                      required
                      style={{ width: "100%" }}
                      isFullWidth
                      {...register("rnd_credits", { required: true })}
                    />
                  </FlexBox>
                </FlexGrow>
              </CustomFlexPlain>
              <CustomFlexPlain>
                <CustomGrowPlain>
                  <Label>Experience credits required</Label>
                </CustomGrowPlain>
                <FlexGrow>
                  <FlexBox
                    space={0}
                    alignItems="end"
                    style={{ width: "100%" }}
                    direction="column"
                  >
                    <Input
                      min={0}
                      type="number"
                      required
                      style={{ width: "100%" }}
                      isFullWidth
                      {...register("xp_credits", { required: true })}
                    />
                  </FlexBox>
                </FlexGrow>
              </CustomFlexPlain>
              <FlexBox>
                <FlexGrow></FlexGrow>
                <Button
                  type="button"
                  onClick={() => setOpen(false)}
                  style={{
                    width: 120,
                    border: "2px solid #64748B",
                    padding: "8px 40px",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  style={{
                    boxShadow: "0 0 5px gray",
                    padding: "8px 40px",
                    background: "#0061ff",
                    color: "white",
                    fontSize: "14px",
                    border: "none",
                    width: 120,
                  }}
                  disabled={isDisableButton}
                >
                  Save
                </Button>
              </FlexBox>
            </FlexGrow>
          </form>
        </CustomFlex>
      </Modal>
    </>
  );
};
export default VersionEdit;
